<template>
	<VerticalLayout>
		<SpoofingGssHeatmap />
		<SpoofingGssAverageHeatmap v-if="sessionStore.debug" />
		<SpoofingVisibilityHeatmap v-if="sessionStore.debug" />
		<Row v-if="sessionStore.debug" :gutter="[16, 8]">
			<Col :span="18" :offset="0">
				<VerticalLayout>
					<StatsChartTitleWithDescription
						titleKey="stats.spoofing.chartTitle"
						descriptionKey="stats.spoofing.chartTitleTooltip"
						titleClass="pd-lg-x pd-md-top"
					/>
					<TimeSeriesChart
						v-if="mountpointSelected != null"
						:isLoading="isLoading"
						:series="series"
						:options="chartOptions"
						:height="500"
					/>
					<EmptyChartFiltersMessage v-else messageKey="stats.spoofing.mountpointPlaceholder" />
				</VerticalLayout>
			</Col>
			<Col :span="6" :offset="0">
				<VerticalLayout>
					<Card :bordered="false">
						<VerticalLayout>
							<div class="pd-vertical">
								<span>{{ $t('stats.spoofing.mountpointLabel') }}</span>
								<Select
									class="width-100"
									v-model:value="mountpointSelected"
									:allowClear="true"
									:options="mountpointOptions"
									:placeholder="$t('stats.spoofing.mountpointPlaceholder')"
									@change="fetchStatsItems"
								/>
							</div>
							<div class="pd-vertical">
								<span>{{ $t('controls.gnssSelect.label') }}</span>
								<Select
									class="width-100"
									v-model:value="gnssSelected"
									:options="gnssOptions"
									@change="fetchStatsItems"
								/>
							</div>
							<div class="pd-vertical">
								<span>{{ $t('stats.spoofing.bandLabel') }}</span>
								<Select
									class="width-100"
									v-model:value="bandsSelected"
									mode="multiple"
									:allowClear="true"
									:options="bandOptions"
									@change="fetchStatsItems"
								/>
							</div>
							<div class="pd-vertical">
								<span>{{ $t('stats.spoofing.elevationCutoffLabel') }}</span>
								<Select
									class="width-100"
									v-model:value="elevationCutoff"
									:options="elevationCutoffOptions"
								/>
							</div>
							<DateRangeChooser v-model="datesRange" quickPeriod="day1" @change="fetchStatsItems" />
						</VerticalLayout>
					</Card>
				</VerticalLayout>
			</Col>
		</Row>
	</VerticalLayout>
</template>

<script lang="ts">
import { ref, computed, onMounted } from 'vue';
import { Row, Col, Card, Select } from 'ant-design-vue';

import Api from '@/api/Api';
import Consts from '@/api/Consts';

import MapHelpers from '@/components/map/utils/MapHelpers';

import { useSessionStore } from '@/stores/session';
import { useBaseStationsStore } from '@/stores/baseStations';
import { useDateRangeAdapter } from '@/utils/useDateRangeAdapter';
import { getBaseOptions, generateSeries, setXAxisRange } from '@/utils/chartsUtils';
import type { AxisTimestampInput } from '@/utils/types/chartsUtilsTypes';
import { SPOOFING_GNSS_CODES } from '@/utils/spoofingVisibilityHeatmapUtils';

import VerticalLayout from '@/components/common/VerticalLayout.vue';
import DateRangeChooser from '@/components/common/DateRangeChooser.vue';
import TimeSeriesChart from '@/components/stats/TimeSeriesChart.vue';
import StatsChartTitleWithDescription from '@/components/stats/StatsChartTitleWithDescription.vue';
import EmptyChartFiltersMessage from '@/components/charts/EmptyChartFiltersMessage.vue';
import SpoofingGssHeatmap from '@/components/stats/SpoofingGssHeatmap.vue';
import SpoofingGssAverageHeatmap from '@/components/stats/SpoofingGssAverageHeatmap.vue';
import SpoofingVisibilityHeatmap from '@/components/stats/SpoofingVisibilityHeatmap.vue';


type NearElevationItem = {
	current_datetime: string;
	mountpoint: string;
	gnss: string;
	band: string;
	kind: 'visible' | 'signal';
	elevations: number[];
};

type ChartPointItem = {
	current_datetime: string;
	seriesKey: string;
	value: number;
};

const DEFAULT_GNSS = 'G';

const ELEVATION_CUTOFF_OPTIONS = [5, 10, 15, 20, 25, 30, 35, 40].map(value => ({
	value,
	label: String(value),
}));

const countAtOrAbove = (elevations: number[], cutoffDeg: number): number => {
	return elevations.filter(elevation => elevation >= cutoffDeg).length;
};

const buildChartItems = (
	items: NearElevationItem[],
	cutoffDeg: number,
): ChartPointItem[] => {
	return items.map(item => {
		const gnssName = MapHelpers.getGnssDataDisplay(item.gnss).name;
		const count = countAtOrAbove(item.elevations || [], cutoffDeg);
		if (item.kind === 'visible') {
			return {
				current_datetime: item.current_datetime,
				seriesKey: 'VISIBLE-' + gnssName,
				value: count,
			};
		}
		return {
			current_datetime: item.current_datetime,
			seriesKey: 'SIGNAL-' + gnssName + '-L' + item.band,
			value: count,
		};
	});
};

const seriesKeySorter = (a: string, b: string): number => {
	const aVisible = a.startsWith('VISIBLE');
	const bVisible = b.startsWith('VISIBLE');
	if (aVisible !== bVisible) {
		return aVisible ? -1 : 1;
	}
	return a.localeCompare(b);
};

export default {
	components: {
		Row, Col, Card, Select,
		VerticalLayout, DateRangeChooser, TimeSeriesChart, StatsChartTitleWithDescription,
		EmptyChartFiltersMessage, SpoofingGssHeatmap, SpoofingGssAverageHeatmap,
		SpoofingVisibilityHeatmap,
	},
	setup() {
		const sessionStore = useSessionStore();
		const baseStationsStore = useBaseStationsStore();
		const { datesRange, datesRangeStr, datesRangeSafe } = useDateRangeAdapter(sessionStore, 1, 0);
		const mountpointSelected = ref<string | undefined>(undefined);
		const gnssSelected = ref(DEFAULT_GNSS);
		const bandsSelected = ref<string[]>(['1', '2', '5']);
		const elevationCutoff = ref(5);
		const { apiData, isLoading, doRequest } = Api.useSpoofingNearElevationGet();

		const mountpointOptions = computed(() => baseStationsStore.mountpointsSelectOptions);
		const gnssOptions = computed(() => SPOOFING_GNSS_CODES.map(code => ({
			value: code,
			label: MapHelpers.getGnssDataDisplay(code).name,
		})));
		const bandOptions = computed(() => Consts.NEAR_ELEVATION_BANDS.map(band => ({
			value: band,
			label: 'L' + band,
		})));

		const fetchStatsItems = () => {
			if (!sessionStore.debug) {
				return;
			}
			if (datesRangeStr.value == null || mountpointSelected.value == null) {
				return;
			}
			doRequest({
				start_date: datesRangeStr.value[0],
				end_date: datesRangeStr.value[1],
				mountpoints: [mountpointSelected.value],
				gnss: [gnssSelected.value],
				bands: bandsSelected.value,
			});
		};

		onMounted(() => {
			fetchStatsItems();
		});

		const chartItems = computed(() => buildChartItems(
			(apiData.value.items || []) as NearElevationItem[],
			elevationCutoff.value,
		));

		const series = computed(() => generateSeries({
			items: chartItems.value,
			getSeriesKeyFn: item => String(item.seriesKey),
			getValueFn: item => typeof item.value === 'number' ? item.value : null,
			seriesStep: apiData.value.period,
			showNullAsZero: false,
			skipNullValues: true,
			keySorter: seriesKeySorter,
		}).series);

		const chartOptions = computed(() => {
			const options = getBaseOptions('spoofing_near_elevation', series.value);
			const rangeStart = datesRangeSafe.value[0];
			const rangeEnd = datesRangeSafe.value[1];
			if (rangeStart != null && rangeEnd != null) {
				setXAxisRange(options, [rangeStart, rangeEnd] as readonly [AxisTimestampInput, AxisTimestampInput]);
			}
			options.yaxis.title.text = 'Count';
			options.yaxis.decimalsInFloat = 0;
			return options;
		});

		return {
			sessionStore,
			isLoading,
			datesRange,
			mountpointSelected,
			mountpointOptions,
			gnssSelected,
			gnssOptions,
			bandsSelected,
			bandOptions,
			elevationCutoff,
			elevationCutoffOptions: ELEVATION_CUTOFF_OPTIONS,
			fetchStatsItems,
			series,
			chartOptions,
		};
	},
};
</script>
